import { ChangeEvent, ReactNode, useId, useMemo, useState } from "react";
import {
  ISelectCardContext,
  ISelectCardOnChangeOptions,
  ISelectCardState,
  SelectCardContext,
} from "./SelectCardContext";

/**
 * Props for the SelectCardProvider component
 * @extends Omit<ISelectCardContext, "radioName" | "selection">
 */
interface ISelectCardProviderProps extends Omit<
  ISelectCardContext,
  "radioName" | "selection"
> {
  children: ReactNode;

  /**
   * Controlled selection state. When provided, the group becomes controlled.
   * Array of `SelectCard` ids that are currently selected.
   */
  value?: Array<string | number>;

  /**
   * Initial selection state for uncontrolled usage.
   * Array of `SelectCard` ids that are selected on first render.
   */
  defaultValue?: Array<string | number>;
}

/**
 * SelectCardProvider component for managing selection state across SelectCard components.
 *
 * Features:
 * - Provides context for SelectCard components to share selection state
 * - Manages selection mode (single vs multiple)
 * - Handles selection state updates and callbacks
 * - Generates unique radio button names for single selection mode
 * - Maintains a set of selected card IDs
 * - Integrates with SelectCardGroup for automatic context provision
 *
 * @example
 * <SelectCardProvider
 *   selectionMode="multiple"
 *   onChange={(e, state) => console.log('Selection changed:', state)}
 * >
 *   <SelectCard id="1" onChange={() => {}}>Option 1</SelectCard>
 *   <SelectCard id="2" onChange={() => {}}>Option 2</SelectCard>
 * </SelectCardProvider>
 */
export const SelectCardProvider = ({
  children,
  defaultValue,
  onChange,
  selectionMode,
  showSelectIndicator,
  value,
}: ISelectCardProviderProps) => {
  const isControlled = value !== undefined;
  const [internalSelection, setInternalSelection] = useState<
    Set<string | number>
  >(() => {
    const ids = defaultValue ?? [];
    return new Set(selectionMode === "single" ? ids.slice(0, 1) : ids);
  });
  const selection = useMemo(() => {
    if (!isControlled) return internalSelection;
    const ids = value ?? [];
    return new Set(selectionMode === "single" ? ids.slice(0, 1) : ids);
  }, [isControlled, value, internalSelection, selectionMode]);

  const radioName = useId();

  const handleChange = (
    e?: ChangeEvent<HTMLInputElement>,
    state?: ISelectCardState,
    options?: ISelectCardOnChangeOptions,
  ) => {
    if (!state || !state.id) {
      if (!options?.internal) {
        onChange?.(e, state);
      }

      return;
    }

    // Update internal selection when uncontrolled
    if (!isControlled) {
      if (selectionMode === "single") {
        setInternalSelection(state.checked ? new Set([state.id]) : new Set());
      } else {
        setInternalSelection((prev) => {
          const next = new Set(prev);
          if (state.checked) {
            next.add(state.id!);
          } else {
            next.delete(state.id!);
          }
          return next;
        });
      }
    }

    if (!options?.internal) {
      onChange?.(e, state);
    }
  };

  const context = {
    hasGroupDefault: defaultValue !== undefined,
    isGroupControlled: isControlled,
    selectionMode,
    onChange: handleChange,
    radioName,
    selection,
    showSelectIndicator,
  };

  return (
    <SelectCardContext.Provider value={context}>
      {children}
    </SelectCardContext.Provider>
  );
};
